import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, InputNumber, Switch, Upload,
  Space, Tag, Typography, message, Popconfirm, DatePicker, Row, Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, PictureOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api, { getBaseURL } from '../services/api';

const { Title, Text } = Typography;

const SCREENS = [
  { value: '/team-config',            label: 'Organizador · Configuração do Time' },
  { value: '/organizer-players',      label: 'Organizador · Jogadores' },
  { value: '/organizer-games',        label: 'Organizador · Jogos' },
  { value: '/rankings',               label: 'Organizador · Ranking' },
  { value: '/frequency',              label: 'Organizador · Frequência' },
  { value: '/cobrancas',              label: 'Organizador · Cobranças' },
  { value: '/my-subscription',        label: 'Organizador · Minha Assinatura' },
  { value: '/my-account',             label: 'Organizador · Minha Conta' },
  { value: '/referrals',              label: 'Organizador · Indicações' },
  { value: '/community/team/profile', label: 'Organizador · Comunidade · Perfil do time' },
  { value: '/community/mural',        label: 'Organizador · Comunidade · Notícias' },
  { value: '/community/amistosos',    label: 'Organizador · Comunidade · Amistosos' },
  { value: '/community/jogos',        label: 'Organizador · Comunidade · Tabela de jogos' },
  { value: '/community/map',          label: 'Organizador · Comunidade · Mapa' },
  { value: '/community/invites',      label: 'Organizador · Comunidade · Convites' },
  { value: '/player/dashboard',       label: 'Jogador · Painel' },
  { value: '/player/map',             label: 'Jogador · Mapa da comunidade' },
  { value: '/player/invites',         label: 'Jogador · Convites' },
  { value: '/player/ratings',         label: 'Jogador · Avaliações' },
  { value: '/player/profile',         label: 'Jogador · Perfil' },
  { value: '/player/referrals',       label: 'Jogador · Indicações' },
  { value: '/player/amistosos',       label: 'Jogador · Amistosos' },
  { value: '/time/dashboard',         label: 'Dono time-comunidade · Painel' },
  { value: '/time/amistosos',         label: 'Dono time-comunidade · Amistosos' },
  { value: '/time/mapa',              label: 'Dono time-comunidade · Mapa' },
  { value: '/time/feed',              label: 'Dono time-comunidade · Feed' },
  { value: '/time/procurar',          label: 'Dono time-comunidade · Procurar jogadores' },
  { value: '/time/convites',          label: 'Dono time-comunidade · Convites' },
  { value: '/perfil-publico-time',    label: 'Público · Perfil do time (comunidade.futezap/time/*)' },
  { value: '/perfil-publico-jogador', label: 'Público · Perfil do jogador (comunidade.futezap/*)' },
  { value: '/comunidade-landing',     label: 'Público · Landing da Comunidade' },
  { value: '/como-funciona-ranking',  label: 'Landing pública · Como funciona o Ranking' },
  { value: '/como-avaliar',           label: 'Landing pública · Como avaliar' },
];

const backendHost = () => getBaseURL().replace(/\/api\/?$/, '');

const AdminBanners = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [desktopFile, setDesktopFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/banners');
      setRows(r.data?.banners || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro ao listar');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setModal({ open: true, editing: null });
    setDesktopFile(null); setMobileFile(null);
    form.resetFields();
    form.setFieldsValue({ active: true, weight: 1, screens: [] });
  };

  const openEdit = (row) => {
    setModal({ open: true, editing: row });
    setDesktopFile(null); setMobileFile(null);
    form.setFieldsValue({
      title: row.title,
      target_url: row.target_url,
      screens: row.screens,
      active: row.active,
      weight: row.weight,
      start_at: row.start_at ? dayjs(row.start_at) : null,
      end_at: row.end_at ? dayjs(row.end_at) : null,
    });
  };

  const submit = async () => {
    const vals = await form.validateFields();
    setSaving(true);
    const fd = new FormData();
    fd.append('title', vals.title);
    if (vals.target_url) fd.append('target_url', vals.target_url);
    fd.append('screens', JSON.stringify(vals.screens || []));
    fd.append('active', String(vals.active !== false));
    fd.append('weight', String(vals.weight || 1));
    if (vals.start_at) fd.append('start_at', vals.start_at.toISOString());
    if (vals.end_at) fd.append('end_at', vals.end_at.toISOString());
    if (desktopFile) fd.append('image_desktop', desktopFile);
    if (mobileFile) fd.append('image_mobile', mobileFile);

    try {
      if (modal.editing) {
        await api.put(`/admin/banners/${modal.editing.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Atualizado');
      } else {
        if (!desktopFile || !mobileFile) { message.error('Envie as duas imagens'); setSaving(false); return; }
        await api.post('/admin/banners', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Criado');
      }
      setModal({ open: false, editing: null });
      load();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/banners/${id}`);
      message.success('Removido');
      load();
    } catch (err) { message.error(err?.response?.data?.error || 'Erro'); }
  };

  const columns = [
    {
      title: 'Preview', dataIndex: 'image_mobile_url', width: 130,
      render: (v) => v ? <img src={backendHost() + v} alt="" style={{ width: 120, borderRadius: 4 }} /> : <PictureOutlined />,
    },
    { title: 'Título', dataIndex: 'title', render: (v, r) => (
      <div>
        <Text strong>{v}</Text>
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
          {(r.screens || []).slice(0, 3).join(' · ')}
          {(r.screens || []).length > 3 && ' · ...'}
        </div>
      </div>
    ) },
    { title: 'Ativo', dataIndex: 'active', width: 80, align: 'center',
      render: (v) => v ? <Tag color="green">ATIVO</Tag> : <Tag>OFF</Tag> },
    { title: 'Peso', dataIndex: 'weight', width: 70, align: 'center' },
    { title: 'Impr. (7d)', dataIndex: 'impressions_7d', width: 100, align: 'right',
      render: (v) => <Text>{v}</Text> },
    { title: 'Cliques (7d)', dataIndex: 'clicks_7d', width: 110, align: 'right',
      render: (v) => <Text strong style={{ color: '#52c41a' }}>{v}</Text> },
    { title: 'CTR (7d)', width: 90, align: 'right',
      render: (_, r) => {
        const imp = r.impressions_7d || 0;
        const clk = r.clicks_7d || 0;
        const ctr = imp > 0 ? (clk / imp * 100) : 0;
        return <Text>{ctr.toFixed(2)}%</Text>;
      } },
    { title: 'Total lifetime', width: 130, align: 'right',
      render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{r.impressions_total} imp · {r.clicks_total} clk</Text> },
    { title: 'Ações', width: 140, align: 'center',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Remover banner?" onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const uploadProps = (setter) => ({
    beforeUpload: (file) => { setter(file); return false; },
    maxCount: 1,
    onRemove: () => setter(null),
  });

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}><PictureOutlined /> Banners</Title>
          <Text type="secondary">Anúncios rotacionados nas telas do organizador. Desktop 300×600, mobile 320×100.</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Novo banner</Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={modal.editing ? `Editar banner #${modal.editing.id}` : 'Novo banner'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        okText="Salvar"
        cancelText="Cancelar"
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="title" label="Título (interno)" rules={[{ required: true, max: 120 }]}>
            <Input placeholder="Ex: Copa Amigos — Setembro" />
          </Form.Item>
          <Form.Item name="target_url" label="URL de destino ao clicar (opcional)">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="screens" label="Telas onde aparece" rules={[{ required: true, message: 'Escolha ao menos 1 tela' }]}>
            <Select mode="multiple" placeholder="Selecione telas" options={SCREENS} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label={<span>Imagem <b>Desktop</b> (300×600)</span>} required={!modal.editing}>
                <Upload {...uploadProps(setDesktopFile)} accept="image/png,image/jpeg,image/webp">
                  <Button icon={<UploadOutlined />}>{desktopFile ? desktopFile.name : 'Selecionar'}</Button>
                </Upload>
                {modal.editing?.image_desktop_url && !desktopFile && (
                  <Text type="secondary" style={{ fontSize: 11 }}>Atual: {modal.editing.image_desktop_url.split('/').pop()}</Text>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<span>Imagem <b>Mobile</b> (320×100)</span>} required={!modal.editing}>
                <Upload {...uploadProps(setMobileFile)} accept="image/png,image/jpeg,image/webp">
                  <Button icon={<UploadOutlined />}>{mobileFile ? mobileFile.name : 'Selecionar'}</Button>
                </Upload>
                {modal.editing?.image_mobile_url && !mobileFile && (
                  <Text type="secondary" style={{ fontSize: 11 }}>Atual: {modal.editing.image_mobile_url.split('/').pop()}</Text>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="weight" label="Peso rotação">
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="active" label="Ativo" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="start_at" label="Início (opc.)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="end_at" label="Fim (opc.)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminBanners;
